import React from "react";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Button, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";

import "dayjs/locale/pl";

dayjs.extend(isoWeek);
dayjs.locale("pl");

interface WeekCalendarStripProps {
  selectedWeekStart: Dayjs;
  onWeekChange: (weekStart: Dayjs) => void;
  onDateSelect?: (date: Dayjs) => void;
}

const dayNames = ["Pn", "Wt", "Śr", "Cz", "Pt", "Sb", "Nd"];

export const WeekCalendarStrip: React.FC<WeekCalendarStripProps> = ({
  selectedWeekStart,
  onWeekChange,
  onDateSelect,
}) => {
  const today = dayjs().startOf("day");

  const goToPreviousWeek = () => {
    onWeekChange(selectedWeekStart.subtract(1, "week"));
  };

  const goToNextWeek = () => {
    onWeekChange(selectedWeekStart.add(1, "week"));
  };

  const goToToday = () => {
    const todayWeekStart = today.startOf("isoWeek");
    onWeekChange(todayWeekStart);
  };

  const weekDays = Array.from({ length: 7 }, (_, i) => selectedWeekStart.add(i, "day"));

  const monthYear = selectedWeekStart.format("MMMM YYYY");

  return (
    <div className="mb-2">
      <div className="mb-3 flex items-center justify-between">
        <Typography.Text strong className="capitalize">
          {monthYear}
        </Typography.Text>
        <div className="flex items-center gap-1">
          <Button
            icon={<LeftOutlined />}
            size="small"
            type="text"
            onClick={goToPreviousWeek}
            aria-label="Poprzedni tydzień"
          />
          <Button size="small" type="text" onClick={goToToday}>
            Dziś
          </Button>
          <Button
            icon={<RightOutlined />}
            size="small"
            type="text"
            onClick={goToNextWeek}
            aria-label="Następny tydzień"
          />
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {weekDays.map((day, index) => {
          const isToday = day.isSame(today, "day");
          const isPast = day.isBefore(today, "day");

          return (
            <button
              key={day.format("YYYY-MM-DD")}
              type="button"
              onClick={() => onDateSelect?.(day)}
              aria-label={day.format("dddd, D MMMM YYYY")}
              className={`flex flex-col items-center rounded-md py-1.5 transition-colors hover:bg-gray-50 ${
                isToday ? "bg-gray-100 font-semibold" : isPast ? "text-gray-400" : ""
              }`}
            >
              <span className="text-xs">{dayNames[index]}</span>
              <span className={`text-sm ${isToday ? "font-semibold" : ""}`}>{day.format("D")}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
